import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useCancelSubscription } from '../../hooks/subscriptions';
import { useSnackbar } from '../SnackbarProvider';
import type { OrganizationSubscription } from '../../types/subscriptions';

interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  currentSubscription: OrganizationSubscription;
}

const CANCELLATION_REASONS = [
  'Too expensive',
  'Missing features',
  'Switching to competitor',
  'No longer needed',
  'Technical issues',
  'Poor customer support',
  'Other',
];

export const CancelSubscriptionDialog = ({
  open,
  onClose,
  organizationId,
  organizationName,
  currentSubscription,
}: CancelSubscriptionDialogProps) => {
  const { showMessage } = useSnackbar();
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const cancelMutation = useCancelSubscription(organizationId);

  const handleCancel = () => {
    if (confirmText !== 'CANCEL') {
      showMessage('Please type CANCEL to confirm', 'warning');
      return;
    }

    if (!selectedReason) {
      showMessage('Please select a cancellation reason', 'warning');
      return;
    }

    const cancellationReason = selectedReason === 'Other' && additionalNotes
      ? `${selectedReason}: ${additionalNotes}`
      : selectedReason;

    cancelMutation.mutate(
      {
        cancellationReason,
      },
      {
        onSuccess: () => {
          showMessage('Subscription canceled successfully', 'success');
          handleClose();
        },
        onError: (error: any) => {
          showMessage(
            error.response?.data?.message || 'Failed to cancel subscription',
            'error'
          );
        },
      }
    );
  };

  const handleClose = () => {
    if (!cancelMutation.isPending) {
      onClose();
      setSelectedReason('');
      setAdditionalNotes('');
      setConfirmText('');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="error" />
          <Typography variant="h6">Cancel Subscription</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Warning:</strong> Canceling the subscription for{' '}
              <strong>{organizationName}</strong> will:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 24 }}>
              <li>
                <Typography variant="body2">
                  Disable access at the end of the current billing period
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Remove all module access when the subscription ends
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Cancel on: {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                </Typography>
              </li>
            </ul>
          </Alert>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Plan
            </Typography>
            <Typography variant="h6">
              {currentSubscription.plan?.name}
            </Typography>
          </Box>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Cancellation Reason</InputLabel>
            <Select
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              label="Cancellation Reason"
            >
              {CANCELLATION_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(selectedReason === 'Other' || selectedReason) && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Notes (Optional)"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              sx={{ mb: 3 }}
              placeholder="Please provide any additional details..."
            />
          )}

          <Box sx={{ p: 2, bgcolor: 'error.lighter', borderRadius: 1, mb: 2 }}>
            <Typography variant="body2" color="error.main" gutterBottom>
              <strong>This action requires confirmation</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Type <strong>CANCEL</strong> below to confirm this action
            </Typography>
            <TextField
              fullWidth
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="Type CANCEL"
              sx={{ mt: 1 }}
              error={confirmText !== '' && confirmText !== 'CANCEL'}
              helperText={
                confirmText !== '' && confirmText !== 'CANCEL'
                  ? 'Must type CANCEL exactly'
                  : ''
              }
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={cancelMutation.isPending}>
          Keep Subscription
        </Button>
        <Button
          onClick={handleCancel}
          variant="contained"
          color="error"
          disabled={
            confirmText !== 'CANCEL' ||
            !selectedReason ||
            cancelMutation.isPending
          }
        >
          {cancelMutation.isPending ? 'Canceling...' : 'Cancel Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
